import { type FC, useState, useEffect, useMemo } from 'react';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';

import { TYPES } from '@/engine/ioc';

import { useInjection } from 'inversify-react';
import { useHighlight } from '@/components/boxes/hooks/useHighlight.ts';
import { useSelect } from '@/components/boxes/hooks/useSelect.ts';
import type { IBaseViewModel } from '@/engine/app/interfaces.ts';
import type { IndicationsBoxController } from '@/components/boxes/indicationsBoxes/indicationBoxController.ts';
import { observer } from 'mobx-react';
import { WorkflowIndicationRow } from '@/components/boxes/indicationsBoxes/workflowIndications/workflowIndicationRow.tsx';
import { CollapsibleContent } from '@/components/boxes/indicationsBoxes/CollapsibleContent.tsx';
import { useGroupItemsVisibleState } from '@/components/boxes/hooks/useGroupItemsVisibleState.ts';

interface IProps {
  indicationsWithLabels: Array<[IBaseViewModel, string]>;
}

export const WorkflowIndicationsBox: FC<IProps> = observer(({ indicationsWithLabels }) => {
  const [isShown, setIsShown] = useState<boolean>(true);
  const mainController = useInjection<IndicationsBoxController>(TYPES.IndicationsBoxController);
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);

  const indications = indicationsWithLabels.map(([indication]) => indication);

  const { isHighlighted } = useHighlight(indications);
  const { isSelected } = useSelect(indications);
  const { visibilityState } = useGroupItemsVisibleState(indications);

  const toggleIsCollapsed = (): void => {
    setIsCollapsed(!isCollapsed);
  };

  useEffect(() => {
    setIsShown(!!indicationsWithLabels.length);
  }, [indicationsWithLabels.length]);

  const allDescriptors = useMemo(() => indications.map((i) => i.descriptor), [indications]);

  return (
    isShown && (
      <TableContainer component={Paper}>
        <Table aria-label="a dense table" size="small">
          <TableBody>
            <CollapsibleContent
              label={'Workflow indications'}
              descriptors={allDescriptors}
              controller={mainController}
              isCollapsed={isCollapsed}
              visibilityState={visibilityState}
              toggleIsCollapsed={toggleIsCollapsed}
            >
              {indicationsWithLabels.map(([indication, label], index) => {
                return (
                  <WorkflowIndicationRow
                    key={index}
                    indication={indication}
                    labelOverride={label}
                    mainController={mainController}
                    isSelected={isSelected}
                    isHighlighted={isHighlighted}
                    index={index}
                  />
                );
              })}
            </CollapsibleContent>
          </TableBody>
        </Table>
      </TableContainer>
    )
  );
});
